import { css, keyframes, styled } from 'styled-components';
import { Root as Group } from '../group/group.styles';
import { media } from '../../themes/breakpoints';

const slideUp = keyframes`
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
`;

const slideDown = keyframes`
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(100%);
    }
`;

/*
 * The backdrop alone fades: its colour and blur, not the modal's opacity, which would fade the
 * sheet with it and show the page through it as it goes.
 */
const backdropIn = keyframes`
    from {
        background-color: transparent;
        backdrop-filter: blur(0);
    }
`;

const backdropOut = keyframes`
    to {
        background-color: transparent;
        backdrop-filter: blur(0);
    }
`;

/** A dialog grows into place from a little smaller as it fades in; leaving, the reverse. */
const dialogIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.96);
    }
`;

const dialogOut = keyframes`
    to {
        opacity: 0;
        transform: scale(0.96);
    }
`;

/*
 * A modal that moves to another of its pages (a step on, or back) shows the new page arriving: on
 * a phone it slides in from the side it was reached from, as an app's screens do; on a desktop it
 * fades in. Each comes twice, under two names, for the reason at PageMove.
 */
const pageKeyframes = (from: string) => keyframes`
    from {
        opacity: 0;
        transform: ${from};
    }
`;
const PAGE_FRAMES = {
    forward: [pageKeyframes('translateX(32px)'), pageKeyframes('translateX(32px)')],
    back: [pageKeyframes('translateX(-32px)'), pageKeyframes('translateX(-32px)')],
    fade: [pageKeyframes('none'), pageKeyframes('none')],
};

/**
 * A move to another page, for the modal's content to animate. `turn` alternates 0 and 1 with each
 * move: the content stays mounted (it is the consumer's), so only a change of animation name
 * restarts the animation, and the twin keyframes give it one.
 */
export type PageMove = { direction: 'forward' | 'back'; turn: 0 | 1 };

const pageAnimation = (frames: keyof typeof PAGE_FRAMES, move: PageMove) => css`
    ${PAGE_FRAMES[frames][move.turn]} 220ms ease-out
`;

/** How long a modal takes to leave: a little quicker than it arrives (250ms). */
export const SHEET_EXIT_MS = 200;

/*
 * Carries the id the dialog is labelled by. It generates no box, so the title stays the flex item
 * of the title row it was before.
 */
export const TitleContent = styled.div`
    display: contents;
`;

interface RootSectionProps {
    $showModal?: boolean;
    /** On its way out: the dialog shrinks and fades (a sheet slides down) as the backdrop fades. */
    $leaving?: boolean;
    /** Animates in when it shows. Not for a modal that is already open as the page arrives. */
    $appear?: boolean;
    /** The last move to another page, if there has been one. */
    $pageMove?: PageMove | null;
    $maxWidth?: string;
    width?: string;
    $padding?: string;
    $zIndex?: number;
}

export const RootSection = styled.section<RootSectionProps>`
    display: ${({ $showModal }) => ($showModal ? 'flex' : 'none')};
    /*
     * Two sizes. The dialog (a phone's sheet) is placed in what the screen shows: a page wider
     * than a phone makes the phone lay it out wider (700×1516 for a 375×812 screen, say), and
     * pinned to that layout a sheet sat at its bottom, far below the screen. The modal follows
     * the visual viewport (--modal-viewport-*, set by the Modal while it's open), so it stays in
     * view however the page is panned or zoomed; until then, the screen's size (vw, and dvh,
     * which also follows the browser's bars; vh where there's no dvh). The backdrop (::before)
     * covers the whole layout, so no part of the page shows through unblurred.
     */
    position: fixed;
    top: var(--modal-viewport-top, 0px);
    left: var(--modal-viewport-left, 0px);
    width: var(--modal-viewport-width, 100vw);
    height: 100vh;
    height: var(--modal-viewport-height, 100dvh);
    /*
     * A pan or pinch that starts on the backdrop doesn't move the page under the modal. The
     * dialog scrolls itself (overflow: auto), so this stops there and its scrolling is its own.
     */
    touch-action: none;
    align-items: ${({ $maxWidth, width }) => ($maxWidth || width ? 'center' : 'stretch')};
    justify-content: center;
    flex-direction: column;
    margin-bottom: 0;
    z-index: ${({ $zIndex }) => ($zIndex ? $zIndex - 1 : 1)};

    /*
     * The backdrop: pinned to the layout's four edges, behind the dialog. Its blur is here, not on
     * the modal: a backdrop-filter on the modal would make it the box this layer is pinned to.
     */
    &::before {
        content: '';
        position: fixed;
        inset: 0;
        z-index: -1;
        background-color: rgba(0, 0, 0, 0.45);
        backdrop-filter: blur(4px);
        animation: ${({ $leaving, $appear }) =>
            $leaving
                ? css`
                      ${backdropOut} ${SHEET_EXIT_MS}ms ease-in forwards
                  `
                : $appear
                  ? css`
                        ${backdropIn} 250ms ease-out
                    `
                  : 'none'};
    }

    & > ${Group} {
        ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth};`}
        ${({ width }) => width && `width: ${width};`}
        background-color: ${({ theme }) => theme.modalBackgroundColor};
        border-radius: ${({ theme }) => theme.spacing(4)};
        padding: ${({ theme, $padding }) => $padding || theme.spacing(6)};
        overflow: auto;
    }

    & > ${Group} > * {
        margin-bottom: ${({ theme, $padding }) => $padding || theme.spacing(6)};
    }

    & > ${Group} > *:last-child {
        margin-bottom: 0;
    }

    /*
     * Opening and closing animate: the backdrop fades (above), and the dialog grows in from a
     * little smaller as it fades in, and back out as it goes. A phone's sheet slides instead
     * (below).
     */
    & > ${Group} {
        animation: ${({ $leaving, $appear }) =>
            $leaving
                ? css`
                      ${dialogOut} ${SHEET_EXIT_MS}ms ease-in forwards
                  `
                : $appear
                  ? css`
                        ${dialogIn} 250ms ease-out
                    `
                  : 'none'};
    }

    & > ${Group} > * {
        animation: ${({ $pageMove }) => ($pageMove ? pageAnimation('fade', $pageMove) : 'none')};
    }

    @media (prefers-reduced-motion: reduce) {
        &::before,
        & > ${Group}, & > ${Group} > * {
            animation: none;
        }
    }

    /*
     * On a phone the modal is a bottom sheet: full width, rounded only at the top, as tall as its
     * content up to a 56px strip of backdrop above it, and scrolling inside past that. The width
     * and max width a consumer sets are for the centred dialog, and give way here.
     *
     * The sheet is a flex column so that a child which scrolls itself (overflow: auto) can shrink
     * to what is left under a header and scroll there, keeping the header in view. Any other child
     * keeps its content height, and the sheet as a whole scrolls past it as before.
     */
    ${media.mobile} {
        justify-content: flex-end;
        align-items: stretch;
        padding-top: ${({ theme }) => theme.spacing(14)};

        & > ${Group} {
            display: flex;
            flex-direction: column;
            width: auto;
            max-width: none;
            max-height: 100%;
            margin: 0;
            border-radius: ${({ theme }) =>
                `${theme.radius.standard}px ${theme.radius.standard}px 0 0`};
            animation: ${({ $leaving, $appear }) =>
                $leaving
                    ? css`
                          ${slideDown} ${SHEET_EXIT_MS}ms ease-in forwards
                      `
                    : $appear
                      ? css`
                            ${slideUp} 250ms ease-out
                        `
                      : 'none'};

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }

        & > ${Group} > * {
            animation: ${({ $pageMove }) =>
                $pageMove ? pageAnimation($pageMove.direction, $pageMove) : 'none'};

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }

        /*
         * Keeps the end of the content clear of the iPhone home indicator. A spacer rather than
         * padding, so it adds to whatever padding the consumer set, in whatever shorthand.
         */
        & > ${Group}::after {
            content: '';
            flex: none;
            height: env(safe-area-inset-bottom, 0px);
        }

        /*
         * The sheet's colour, on below it. The sheet ends where the visual viewport does, but iOS
         * Safari draws its translucent bars over the page below that: the address bar above the
         * keyboard, say. Without this, what's under them is the backdrop and the page. Here, not
         * on the sheet, as the sheet scrolls and would clip it. It fades in and out with the
         * backdrop, so it isn't there before the sheet has slid up or after it has slid away.
         */
        &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            height: 100vh;
            background-color: ${({ theme }) => theme.modalBackgroundColor};
            pointer-events: none;
            animation: ${({ $leaving, $appear }) =>
                $leaving
                    ? css`
                          ${backdropOut} ${SHEET_EXIT_MS}ms ease-in forwards
                      `
                    : $appear
                      ? css`
                            ${backdropIn} 250ms ease-out
                        `
                      : 'none'};

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }
    }

    & > ${Group} > * > *,
    & > ${Group} > * > ${TitleContent} > * {
        margin: 0;
    }
`;

export const BodyContent = styled.div``;
export const ActionContent = styled.div``;
